import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { CartContext } from '@/App';
import { toast } from 'sonner';
import { ShoppingCart, Clock, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { addToCart } = useContext(CartContext);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`)
      ]);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category_id === selectedCategory);

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} ajouté au panier`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMy4zMTQgMi42ODYtNiA2LTZzNiAyLjY4NiA2IDYtMi42ODYgNi02IDYtNi0yLjY4Ni02LTZ6TTEyIDM2YzAtMy4zMTQgMi42ODYtNiA2LTZzNiAyLjY4NiA2IDYtMi42ODYgNi02IDYtNi0yLjY4Ni02LTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Sparkles size={16} className="text-amber-400" />
            <span className="text-sm font-medium">Spécialités Italiennes d'Exception</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500" data-testid="hero-title">
            Le Comptoir de Clément
          </h1>
          <p className="text-xl text-gray-300 mb-2 font-light">Brive-la-Gaillarde</p>
          <p className="text-lg text-gray-400 mb-8">Planches, Pâtes Fraîches & Épicerie Fine</p>
          <div className="inline-block bg-white/95 backdrop-blur-sm text-gray-900 px-8 py-4 rounded-2xl shadow-2xl">
            <p className="font-semibold mb-2 text-sm uppercase tracking-wide text-gray-600">Horaires de retrait</p>
            <div className="text-sm space-y-1 text-gray-700">
              <p><span className="font-medium">Mar-Jeu:</span> 10h-13h</p>
              <p><span className="font-medium">Ven-Sam:</span> 10h-18h</p>
              <p><span className="font-medium">Dimanche:</span> 10h-15h</p>
              <p className="text-red-600 font-semibold mt-2">Lundi: Fermé</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Description Section */}
        <div className="mb-16 bg-white rounded-3xl shadow-lg p-8 md:p-12 border border-gray-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Bienvenue au Comptoir de Clément</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto rounded-full"></div>
            </div>
            
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p className="text-lg">
                Situé au sein de <span className="font-semibold text-slate-900">La Halle Gaillarde</span>, temple de la gastronomie corrézienne, nous proposons une <span className="font-semibold">cuisine de marché réalisée sur place avec des produits frais</span>. Amateurs de truffes et de produits italiens et espagnols seront ravis.
              </p>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="text-2xl mr-2">🥓</span>
                  Charcuterie Italienne
                </h3>
                <p className="text-gray-700">
                  Jambon à la truffe, Jambon de Parme, Speck, Guanciale, Coppa IGP, Mortadelle, Saucisson à la truffe, Bellota, Serrano.
                </p>
              </div>

              <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-slate-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="text-2xl mr-2">🧀</span>
                  Fromages Haut de Gamme
                </h3>
                <p className="text-gray-700">
                  Brie à la truffe, Pecorino au poivre, Burrata di Bufala, Mozzarella di Bufala, Parmigiano Reggiano.
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="text-2xl mr-2">🍝</span>
                  La Cuisine
                </h3>
                <p className="text-gray-700">
                  Coquillettes au jambon à la truffe, Linguine Carbonara, Pesto, Omelettes aux cèpes et à la truffe fraîche, planches de charcuterie.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="text-2xl mr-2">🎉</span>
                  Prestation Traiteur
                </h3>
                <p className="text-gray-700">
                  Nous pouvons vous proposer une prestation traiteur adaptée à vos envies : Charcuterie, fromages et mignardises salées et sucrées.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Nos Produits</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-xl transition-all font-medium ${
                selectedCategory === 'all'
                  ? 'bg-gradient-to-r from-slate-800 to-slate-900 text-white shadow-lg transform scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
              data-testid="category-all"
            >
              Tous
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-6 py-3 rounded-xl transition-all font-medium ${
                  selectedCategory === cat.id
                    ? 'bg-gradient-to-r from-slate-800 to-slate-900 text-white shadow-lg transform scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
                data-testid={`category-${cat.slug}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map(product => (
            <div
              key={product.id}
              className="product-card bg-white rounded-2xl shadow-sm overflow-hidden group"
              data-testid={`product-card-${product.id}`}
            >
              <div className="h-64 overflow-hidden relative">
                <img
                  src={product.image_url || 'https://via.placeholder.com/400x300?text=Produit'}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {product.requires_24h_notice && (
                  <div className="absolute top-4 left-4 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1 shadow-lg">
                    <Clock size={12} />
                    <span>24h</span>
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2 text-gray-900" data-testid={`product-name-${product.id}`}>{product.name}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-3xl font-bold text-gray-900" data-testid={`product-price-${product.id}`}>
                      {product.price.toFixed(2)}
                    </span>
                    <span className="text-xl text-gray-600">€</span>
                  </div>
                  <button
                    onClick={() => handleAddToCart(product)}
                    className="flex items-center space-x-2 bg-gradient-to-r from-slate-800 to-slate-900 text-white px-5 py-3 rounded-xl hover:from-slate-900 hover:to-black transition-all shadow-sm hover:shadow-lg transform hover:-translate-y-0.5"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingCart size={18} />
                    <span className="font-medium">Ajouter</span>
                  </button>
                </div>

                {product.stock < 5 && product.stock > 0 && (
                  <p className="text-orange-600 text-sm font-medium">Plus que {product.stock} en stock</p>
                )}
                {product.stock === 0 && (
                  <p className="text-red-600 text-sm font-medium">Rupture de stock</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">Aucun produit trouvé dans cette catégorie</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;